/**
 * A workspace tab's last COMPLETED run — the unit the attachment is made of.
 *
 * THE FAILURE THIS EXISTS FOR. A user runs a search in a workspace tab, gets
 * 47 products, switches to the chat and asks "which of these lack the elderly
 * warning?" — and CEPI answers across the whole corpus, because nothing was
 * ever attached. The answer names real drugs and reads perfectly well. It is
 * the exact failure worth caring about: a correct-looking answer from the
 * wrong universe, with nothing on screen to say so.
 *
 * THE RULE. Attaching is always an explicit user choice. A finished run is
 * only REPORTED here; CEPI offers it ("Answer from these N results") and the
 * user attaches it with one click. Nothing attaches by itself: not a
 * completed run, not a tab switch, not an Investigator job finishing. An
 * auto-attached "whichever search finished last" would answer about set B
 * while the user reads set A, which is the failure above.
 *
 * What survives unchanged is the corpus→rows-key mapping below, which is
 * load-bearing and has been wrong in production once already.
 */
import type { Capability } from '../types/qa';

/**
 * Which Cogni corpus a result set came from.
 *
 * Cogni is ONE surface over six corpora that do not share a response shape —
 * labels return drug-level rows, the rest return documents — so the rows key
 * cannot be inferred from the body. It travels with the set, all the way to
 * `cepi_result_capture.capture(corpus=...)`.
 */
export type CogniCorpus =
  | 'labels' | 'fda_letters' | 'approval_letters' | 'ci' | 'sba';

/** Where the rows live, per corpus. Mirrors `_COGNI_ROWS_KEY` on the backend. */
const COGNI_ROWS_KEY: Record<CogniCorpus, string> = {
  labels: 'drug_results',
  fda_letters: 'results',
  approval_letters: 'results',
  ci: 'documents',
  sba: 'documents',
};

/**
 * Rows in a capture-eligible response.
 *
 * The keys mirror `_ROWS_KEY` in `cepi_result_capture.py` — Cogni's
 * `/v2/search/drugs` returns `drug_results`, Keyword returns `results`. That
 * pair has been wrong once already: both this side and the capture read
 * `results` for Cogni, both test suites mocked the wrong assumption, and every
 * Cogni capture silently stored nothing. If the backend key ever moves, this
 * is the second place to change.
 */
export function countRows(
  capability: Capability, response: unknown, corpus?: CogniCorpus | undefined,
): number {
  if (!response || typeof response !== 'object') return 0;
  const body = response as Record<string, unknown>;
  const key = capability === 'cogni'
    ? COGNI_ROWS_KEY[corpus ?? 'labels']
    : 'results';
  const rows = body[key];
  return Array.isArray(rows) ? rows.length : 0;
}

/** Mirrors `MAX_ROWS` in `cepi_result_capture.py`: more rows than this are cut. */
export const CAPTURE_MAX_ROWS = 300;

/**
 * Whether attaching this response hands CEPI EVERY match — the "all" vs "top"
 * on the chat's button. Mirrors `proves_absence` in `cepi_result_capture.py`;
 * keep the two in step. Cogni is ranked, so never; Keyword only when nothing
 * windowed, truncated, or left rows on a later page.
 */
export function isCompleteSet(
  capability: Capability, response: unknown, corpus?: CogniCorpus | undefined,
): boolean {
  if (capability !== 'keyword' || !response || typeof response !== 'object') return false;
  const body = response as Record<string, unknown>;
  const rows = countRows(capability, response, corpus);
  const gaps = body['coverage_gap_drug_ids'];
  return !(
    body['count_is_windowed'] || body['truncated'] || body['count_failed']
    || (Array.isArray(gaps) && gaps.length > 0)
    || rows > CAPTURE_MAX_ROWS
    || rows < (Number(body['total_count']) || 0)
  );
}

/**
 * A SNAPSHOT run — Cogni or Keyword. The rows are copied into a `result_set`
 * and bounded on the way in, because the search that produced them cannot be
 * replayed: re-running would attach a different set than the one on screen.
 */
export interface RunResultSet {
  kind: 'set';
  capability: Capability;
  /** Cogni only; undefined for Keyword, which has one shape. */
  corpus?: CogniCorpus | undefined;
  /** Rows on screen. Shown to the user, so it must match what they can count. */
  count: number;
  queryText: string;
  /** Keyword only: the page's QUERY line (`pharm_class:"X" AND region:US`), when it differs from the bare terms. */
  searchQuery?: string | undefined;
  /** Provenance for "complete over what" — posted with the capture. */
  request: unknown;
  /** The response body as received, posted verbatim. */
  response: unknown;
  seq: number;
}

/**
 * A POINTER run — Investigator. `attachResult` writes a `research_job` ref and
 * `cepi_results_read` reads `research_job_results` live, so there is nothing
 * to snapshot: the rows are already durable and addressable.
 */
export interface RunResultJob {
  kind: 'job';
  jobId: string;
  count: number;
  queryText: string;
  seq: number;
}

export type RunResult = RunResultSet | RunResultJob;

let seq = 0;

/**
 * Monotonic id for a completed run.
 *
 * This is what separates "the user re-ran the search in this tab" from "the
 * user switched away and back". Focus changes are frequent; only a NEW seq
 * re-attaches, so merely looking at the attached tab again costs nothing.
 */
export function nextRunSeq(): number {
  seq += 1;
  return seq;
}
